import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { STRINGS } from "@/lib/i18n";
import { FORMATS, FORMATS_EN, LEVELS, LEVELS_EN, STYLES, STYLES_EN, IMAGES } from "@/lib/data";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PayPalCheckout, { usePayPalConfig } from "@/components/PayPalCheckout";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Booking({ presetFormat, onOpenAuth }) {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const t = STRINGS[lang].booking;
  const formats = lang === "en" ? FORMATS_EN : FORMATS;
  const levels = lang === "en" ? LEVELS_EN : LEVELS;
  const stylesList = lang === "en" ? STYLES_EN : STYLES;
  const paypalConfig = usePayPalConfig();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    format: "aula-particular",
    level: "iniciante",
    style: "blues",
    date: "",
    time: "",
    notes: "",
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (presetFormat) setForm((f) => ({ ...f, format: presetFormat, time: "" }));
  }, [presetFormat]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
  }, [user]);

  useEffect(() => {
    if (!form.date) return;
    setLoadingSlots(true);
    setForm((f) => ({ ...f, time: "" }));
    api
      .get(`/availability?date=${form.date}`)
      .then((res) => setSlots(res.data.slots))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [form.date]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time) {
      toast.error(t.pickTime);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", form);
      setConfirmation(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    const [y, m, d] = iso.split("-");
    return lang === "en" ? `${m}/${d}/${y}` : `${d}/${m}/${y}`;
  };

  const formatName = (id) => formats.find((f) => f.id === id)?.name || id;
  const levelName = (id) => levels.find((l) => l.id === id)?.name || id;
  const styleName = (id) => stylesList.find((s) => s.id === id)?.name || id;
  const trialPrice = confirmation && paypalConfig?.trialPrices ? paypalConfig.trialPrices[confirmation.format] : null;

  return (
    <section id="agendar" className="border-y border-white/5 bg-[#070B14] py-28 lg:py-36" data-testid="booking-section">
      <div className="mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2 lg:items-start">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
          <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            {t.title[0]}
            <em className="italic text-blue-400">{t.title[1]}</em>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300/90">{t.copy}</p>
          <div className="relative mt-12 hidden overflow-hidden rounded-2xl border border-white/10 lg:block" style={{ clipPath: "polygon(0 0, 100% 3%, 100% 100%, 0 97%)" }}>
            <img src={IMAGES.fretboard} alt="Guitar fretboard detail under blue stage light" className="h-64 w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-transparent to-transparent" aria-hidden="true" />
          </div>
          <div className="mt-10 space-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            {t.notes.map((note) => (
              <p key={note}>{note}</p>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl border border-white/10 bg-[#0B101C]/80 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.4)] backdrop-blur-xl sm:p-9"
        >
          {confirmation ? (
            <div className="py-6 text-center" data-testid="booking-confirmation">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-blue-500/40 bg-blue-500/10">
                <CalendarCheck className="h-6 w-6 text-blue-400" />
              </span>
              <h3 className="mt-6 font-serif text-2xl text-blue-50">{t.confirmationTitle}</h3>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                {formatName(confirmation.format)} · {levelName(confirmation.level)} · {styleName(confirmation.style)}
                <br />
                <span className="text-blue-300">
                  {formatDate(confirmation.date)} · {confirmation.time}
                </span>
              </p>
              <p className="mt-4 text-sm text-slate-500">
                {t.confirmationEmail} {confirmation.email}.
              </p>

              <div className="mx-auto mt-8 max-w-xs border-t border-white/5 pt-6" data-testid="booking-payment-block">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.payTitle}</p>
                {trialPrice && (
                  <p className="mt-2 font-serif text-3xl text-blue-300" data-testid="booking-payment-price">
                    US$ {Number(trialPrice)}
                  </p>
                )}
                <div className="mt-4">
                  <PayPalCheckout
                    description={`IGL — ${formatName(confirmation.format)} (${formatDate(confirmation.date)} ${confirmation.time})`}
                    amount={trialPrice}
                    testid="paypal-trial-link"
                  />
                </div>
              </div>

              <div className="mt-8 flex flex-col items-center gap-3">
                {!user && (
                  <button
                    onClick={onOpenAuth}
                    data-testid="booking-create-account-button"
                    className="rounded-full bg-blue-400 px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-[#05070D] transition-colors duration-300 hover:bg-blue-300"
                  >
                    {t.createAccount}
                  </button>
                )}
                <button
                  onClick={() => {
                    setConfirmation(null);
                    setForm((f) => ({ ...f, date: "", time: "", notes: "" }));
                  }}
                  data-testid="booking-new-button"
                  className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 transition-colors duration-300 hover:text-blue-300"
                >
                  {t.bookAnother}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.name}</label>
                  <input
                    required
                    value={form.name}
                    onChange={set("name")}
                    data-testid="booking-name-input"
                    placeholder={t.namePh}
                    className="w-full rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.email}</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    data-testid="booking-email-input"
                    placeholder={t.emailPh}
                    className="w-full rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.phone}</label>
                <input
                  value={form.phone}
                  onChange={set("phone")}
                  data-testid="booking-phone-input"
                  placeholder={t.phonePh}
                  className="w-full rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50"
                />
              </div>

              <div>
                <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.format}</label>
                <div className="grid grid-cols-2 gap-2">
                  {formats.map((format) => (
                    <button
                      type="button"
                      key={format.id}
                      onClick={() => set("format")(format.id)}
                      data-testid={`booking-format-${format.id}`}
                      className={`rounded-lg border px-3 py-3 text-left text-xs font-medium transition-colors duration-300 ${
                        form.format === format.id
                          ? "border-blue-500/60 bg-blue-500/10 text-blue-300"
                          : "border-white/10 bg-[#05070D] text-slate-400 hover:border-white/25"
                      }`}
                    >
                      {format.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.level}</label>
                  <Select value={form.level} onValueChange={(v) => set("level")(v)}>
                    <SelectTrigger data-testid="booking-level-select" className="border-white/10 bg-[#05070D] text-sm text-slate-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((level) => (
                        <SelectItem key={level.id} value={level.id} data-testid={`booking-level-option-${level.id}`}>
                          {level.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.style}</label>
                  <Select value={form.style} onValueChange={(v) => set("style")(v)}>
                    <SelectTrigger data-testid="booking-style-select" className="border-white/10 bg-[#05070D] text-sm text-slate-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {stylesList.map((style) => (
                        <SelectItem key={style.id} value={style.id} data-testid={`booking-style-option-${style.id}`}>
                          {style.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.date}</label>
                <input
                  required
                  type="date"
                  min={todayISO()}
                  value={form.date}
                  onChange={set("date")}
                  data-testid="booking-date-input"
                  className="w-full rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 focus:border-blue-500/50"
                />
              </div>

              <div>
                <label className="mb-3 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.time}</label>
                {!form.date ? (
                  <p className="rounded-lg border border-dashed border-white/10 px-4 py-4 text-center text-xs text-slate-500" data-testid="booking-slots-hint">
                    {t.slotsHint}
                  </p>
                ) : loadingSlots ? (
                  <p className="flex items-center justify-center gap-2 py-4 text-xs text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> {t.slotsLoading}
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="booking-slots">
                    {slots.map((slot) => (
                      <button
                        type="button"
                        key={slot.time}
                        disabled={!slot.available}
                        onClick={() => set("time")(slot.time)}
                        data-testid={`booking-slot-${slot.time.replace(":", "")}`}
                        className={`rounded-lg border px-2 py-2.5 font-mono text-xs transition-colors duration-300 ${
                          !slot.available
                            ? "cursor-not-allowed border-white/5 text-slate-700 line-through"
                            : form.time === slot.time
                              ? "border-blue-500/60 bg-blue-500/10 text-blue-300"
                              : "border-white/10 bg-[#05070D] text-slate-300 hover:border-white/25"
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.notesLabel}</label>
                <textarea
                  value={form.notes}
                  onChange={set("notes")}
                  data-testid="booking-notes-input"
                  rows={3}
                  placeholder={t.notesPh}
                  className="w-full resize-none rounded-lg border border-white/10 bg-[#05070D] px-4 py-3 text-sm text-slate-100 outline-none transition-colors duration-300 placeholder:text-slate-600 focus:border-blue-500/50"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit-button"
                className="flex w-full items-center justify-center gap-3 rounded-full bg-blue-400 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#05070D] transition-colors duration-300 hover:bg-blue-300 disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? t.submitting : t.submit}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
